import type {
  AssistantPresentationSection,
  AssistantPresentationTableCell,
} from '@triedge/ai-assistant-ui';
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantArtifactPreview,
  AdmissionsCitation,
} from './types';
import {
  resolveCitationApplicationId,
  resolveCitationEntityLabel,
  resolveCitationRouteLabel,
  resolveCitationRouteTarget,
} from './citation-routing';

type RouteLookupEntry = {
  href: string;
  label: string;
  applicationCode: string | null;
  applicationId: string | null;
};

type ResponseSurfaceProjection = {
  mainText: string;
  projectedSections: AssistantPresentationSection[];
  suppressRelatedLinks: boolean;
  rawDataText: string | null;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MARKDOWN_TABLE_ROW_PATTERN = /^\|.+\|\s*$/;
const MARKDOWN_TABLE_SEPARATOR_PATTERN = /^\|\s*:?-{2,}:?\s*(?:\|\s*:?-{2,}:?\s*)+\|\s*$/;
const APPLICATION_CODE_PATTERN = /\b(?:APP|FA)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*\b/i;
const EXACT_TARGET_BLOCK_PATTERN = /(?:^|\n)\s*(?:\d+\.\s*)?(?:Applicant|Application)\s+ID:\s*([0-9a-f-]{8,}|[A-Z0-9-]+)(?:\s*\(Code:\s*([A-Z0-9-]+)\))?([\s\S]*?)(?=(?:\n\s*(?:\d+\.\s*)?(?:Applicant|Application)\s+ID:)|\n\s*These applicants\b|\n\s*These applications\b|$)/gi;
const ACTIONABLE_GENERIC_LABEL_PATTERN = /^(?:open (?:application|family details|student details|resource)|application|applicant|record)$/i;

const normalizeText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const cleanupProjectedText = (value: string): string => value
  .replace(/\n{3,}/g, '\n\n')
  .split('\n')
  .map((line) => line.trimEnd())
  .join('\n')
  .trim();

const parseMarkdownCells = (line: string): string[] => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => normalizeText(cell));

const parseChartValue = (value: string): number | null => {
  const normalized = value.replace(/,/g, '').replace(/[^0-9.-]/g, '');
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toLabelFromAction = (action: AdmissionsAIContextualAction): string | null => {
  const payload = action.payload && typeof action.payload === 'object' ? action.payload : null;
  const candidate = normalizeText(
    action.entityRef?.label
    ?? (payload ? payload.entityLabel : null)
    ?? action.label
  )
    .replace(/^open\s+/i, '')
    .trim();
  if (!candidate || ACTIONABLE_GENERIC_LABEL_PATTERN.test(candidate)) {
    return null;
  }
  return candidate;
};

const toRouteFromAction = (action: AdmissionsAIContextualAction): string | null => {
  const payload = action.payload && typeof action.payload === 'object' ? action.payload : null;
  return normalizeText(
    action.targetRoute
    ?? (payload ? payload.targetRoute : null)
    ?? (payload ? payload.href : null)
  ) || null;
};

const toApplicationCodeFromAction = (action: AdmissionsAIContextualAction): string | null => {
  const payload = action.payload && typeof action.payload === 'object' ? action.payload : null;
  return normalizeText(payload ? payload.applicationCode : null) || null;
};

const toApplicationIdFromAction = (action: AdmissionsAIContextualAction): string | null => {
  const payload = action.payload && typeof action.payload === 'object' ? action.payload : null;
  const explicit = normalizeText(
    action.entityRef?.id
    ?? (payload ? payload.entityId : null)
  );
  if (UUID_PATTERN.test(explicit)) {
    return explicit;
  }
  const route = toRouteFromAction(action);
  const routeMatch = route?.match(/\/([0-9a-f-]{36})(?:[/?#]|$)/i);
  return routeMatch?.[1] ?? null;
};

const buildRouteLookup = (input: {
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
}) => {
  const byApplicationId = new Map<string, RouteLookupEntry>();
  const byApplicationCode = new Map<string, RouteLookupEntry>();

  const storeEntry = (entry: RouteLookupEntry) => {
    if (entry.applicationId) {
      byApplicationId.set(entry.applicationId.toLowerCase(), byApplicationId.get(entry.applicationId.toLowerCase()) ?? entry);
    }
    if (entry.applicationCode) {
      byApplicationCode.set(entry.applicationCode.toLowerCase(), byApplicationCode.get(entry.applicationCode.toLowerCase()) ?? entry);
    }
  };

  input.citations.forEach((citation) => {
    const target = resolveCitationRouteTarget(citation);
    if (!target) {
      return;
    }
    const label = normalizeText(resolveCitationEntityLabel(citation))
      || normalizeText(resolveCitationRouteLabel(citation, target))
      || 'Applicant';
    storeEntry({
      href: target.href,
      label,
      applicationCode: normalizeText(citation.applicationCode) || null,
      applicationId: resolveCitationApplicationId(citation),
    });
  });

  (input.actions ?? []).forEach((action) => {
    const href = toRouteFromAction(action);
    if (!href) {
      return;
    }
    storeEntry({
      href,
      label: toLabelFromAction(action) || 'Applicant',
      applicationCode: toApplicationCodeFromAction(action),
      applicationId: toApplicationIdFromAction(action),
    });
  });

  return {
    find(params: {
      applicationId?: string | null;
      applicationCode?: string | null;
    }): RouteLookupEntry | null {
      const byCode = normalizeText(params.applicationCode).toLowerCase();
      if (byCode && byApplicationCode.has(byCode)) {
        return byApplicationCode.get(byCode) ?? null;
      }
      const byId = normalizeText(params.applicationId).toLowerCase();
      if (byId && byApplicationId.has(byId)) {
        return byApplicationId.get(byId) ?? null;
      }
      return null;
    },
  };
};

const projectMarkdownChartSurface = (mainText: string): {
  nextText: string;
  section: AssistantPresentationSection | null;
  rawDataText: string | null;
} => {
  const lines = mainText.split('\n');
  let headerIndex = -1;
  for (let index = 0; index < lines.length - 1; index += 1) {
    if (MARKDOWN_TABLE_ROW_PATTERN.test(lines[index] ?? '') && MARKDOWN_TABLE_SEPARATOR_PATTERN.test(lines[index + 1] ?? '')) {
      headerIndex = index;
      break;
    }
  }

  if (headerIndex < 0) {
    return { nextText: mainText, section: null, rawDataText: null };
  }

  let endIndex = headerIndex + 2;
  while (endIndex < lines.length && MARKDOWN_TABLE_ROW_PATTERN.test(lines[endIndex] ?? '')) {
    endIndex += 1;
  }

  const rowLines = lines.slice(headerIndex, endIndex);
  if (rowLines.length < 3) {
    return { nextText: mainText, section: null, rawDataText: null };
  }

  const dataRows = rowLines.slice(2).map((line) => parseMarkdownCells(line));
  const chartItems = dataRows
    .map((cells, index) => {
      const label = normalizeText(cells[0]);
      const value = parseChartValue(cells[1] ?? '');
      if (!label || value == null) {
        return null;
      }
      return {
        id: `projected-chart-row-${index + 1}`,
        label,
        value,
        tone: 'primary' as const,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  if (chartItems.length < 2) {
    return { nextText: mainText, section: null, rawDataText: null };
  }

  let chartTitle: string | null = null;
  let titleIndex = -1;
  for (let index = headerIndex - 1; index >= 0; index -= 1) {
    const candidate = normalizeText(lines[index]);
    if (!candidate) {
      continue;
    }
    if (!candidate.endsWith(':')) {
      chartTitle = candidate;
      titleIndex = index;
    }
    break;
  }

  const beforeLines = lines.slice(0, titleIndex >= 0 ? titleIndex : headerIndex);
  const afterLines = lines
    .slice(endIndex)
    .filter((line) => !/^\s*This chart shows\b/i.test(line));
  const nextText = cleanupProjectedText([...beforeLines, ...afterLines].join('\n'));
  const rawDataText = cleanupProjectedText([
    titleIndex >= 0 ? lines[titleIndex] : '',
    ...rowLines,
  ].filter(Boolean).join('\n')) || null;

  return {
    nextText,
    section: {
      type: 'chart',
      title: null,
      chartTitle: chartTitle ?? 'Chart preview',
      items: chartItems,
      maxValue: Math.max(...chartItems.map((item) => item.value)),
    },
    rawDataText,
  };
};

const projectExactTargetTableSurface = (input: {
  mainText: string;
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
}): {
  nextText: string;
  section: AssistantPresentationSection | null;
  suppressRelatedLinks: boolean;
  rawDataText: string | null;
} => {
  const lookup = buildRouteLookup({
    citations: input.citations,
    actions: input.actions,
  });
  const matches = [...input.mainText.matchAll(EXACT_TARGET_BLOCK_PATTERN)];
  if (matches.length === 0) {
    return {
      nextText: input.mainText,
      section: null,
      suppressRelatedLinks: false,
      rawDataText: null,
    };
  }

  const rows = matches.map((match, index) => {
    const applicationId = normalizeText(match[1]);
    const applicationCode = normalizeText(match[2]) || null;
    const body = String(match[3] ?? '');
    const status = normalizeText(body.match(/Status:\s*([^\n]+)/i)?.[1]) || 'Needs review';
    const lastActivity = normalizeText(body.match(/Last activity:\s*([^\n]+)/i)?.[1]) || 'Not available';
    const linkedRecord = lookup.find({
      applicationId,
      applicationCode,
    });
    const applicantCell: AssistantPresentationTableCell = {
      text: linkedRecord?.label || `Applicant ${index + 1}`,
      href: linkedRecord?.href ?? null,
    };
    return {
      id: applicationCode ?? applicationId ?? `projected-applicant-${index + 1}`,
      cells: [
        applicantCell,
        status,
        lastActivity,
      ],
      linked: Boolean(linkedRecord?.href),
    };
  });

  const intro = cleanupProjectedText(input.mainText.slice(0, matches[0]?.index ?? 0));
  const lastMatch = matches[matches.length - 1];
  const finalMatchStart = lastMatch?.index ?? 0;
  const finalMatchLength = lastMatch?.[0]?.length ?? 0;
  const conclusion = cleanupProjectedText(input.mainText.slice(finalMatchStart + finalMatchLength));
  const nextText = cleanupProjectedText([intro, conclusion].filter(Boolean).join('\n\n'));
  const rawDataText = cleanupProjectedText(
    matches
      .map((match) => cleanupProjectedText(String(match[0] ?? '')))
      .filter(Boolean)
      .join('\n\n')
  ) || null;

  return {
    nextText,
    section: {
      type: 'table',
      title: null,
      columns: [
        { id: 'applicant', label: 'Applicant' },
        { id: 'status', label: 'Status' },
        { id: 'last-activity', label: 'Last activity' },
      ],
      rows: rows.map(({ linked, ...row }) => row),
    },
    suppressRelatedLinks: rows.every((row) => row.linked),
    rawDataText,
  };
};

export const projectAdmissionsResponseSurface = (input: {
  mainText: string;
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
  artifactPreview: AdmissionsAssistantArtifactPreview | null;
}): ResponseSurfaceProjection => {
  let nextText = String(input.mainText ?? '').trim();
  const projectedSections: AssistantPresentationSection[] = [];
  let suppressRelatedLinks = false;
  const rawDataBlocks: string[] = [];

  const hasStructuredArtifact = Boolean(
    input.artifactPreview?.workbookArtifact
	    || input.artifactPreview?.spreadsheetDownloadArtifact
	    || input.artifactPreview?.chartArtifact
	    || input.artifactPreview?.visualization3DArtifact
	    || input.artifactPreview?.landingPageArtifact
    || input.artifactPreview?.diagramArtifact
	    || (input.artifactPreview?.artifactCards?.length ?? 0) > 0
	    || (input.artifactPreview?.chartPanels?.length ?? 0) > 0
	    || Boolean(input.artifactPreview?.visualization3DSpace)
	    || (input.artifactPreview?.visualization3DPanels?.length ?? 0) > 0
	  );

  if (!hasStructuredArtifact && nextText.split('\n').some((line) => MARKDOWN_TABLE_ROW_PATTERN.test(line))) {
    const projectedChart = projectMarkdownChartSurface(nextText);
    nextText = projectedChart.nextText;
    if (projectedChart.section) {
      projectedSections.push(projectedChart.section);
    }
    if (projectedChart.rawDataText) {
      rawDataBlocks.push(projectedChart.rawDataText);
    }
  }

  if (!APPLICATION_CODE_PATTERN.test(nextText) && !/\b(?:Applicant|Application)\s+ID:/i.test(nextText)) {
    return {
      mainText: nextText,
      projectedSections,
      suppressRelatedLinks,
      rawDataText: cleanupProjectedText(rawDataBlocks.join('\n\n')) || null,
    };
  }

  const projectedTable = projectExactTargetTableSurface({
    mainText: nextText,
    citations: input.citations,
    actions: input.actions,
  });
  nextText = projectedTable.nextText;
  if (projectedTable.section) {
    projectedSections.push(projectedTable.section);
    suppressRelatedLinks = projectedTable.suppressRelatedLinks;
  }
  if (projectedTable.rawDataText) {
    rawDataBlocks.push(projectedTable.rawDataText);
  }

  return {
    mainText: nextText,
    projectedSections,
    suppressRelatedLinks,
    rawDataText: cleanupProjectedText(rawDataBlocks.join('\n\n')) || null,
  };
};
